import {
  URL_PRODUCTS_GUARANTEED_HIRE,
  URL_PRODUCT_TERMS,
  URL_SUPPORT_TERMS,
} from '@seek/adv-constants';
import type { JobCategoryRef } from '@seek/job-category';
import { isEmail } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  Button,
  IconLocation,
  Stack,
  Text,
  TextField,
  TextLink,
} from 'braid-design-system';
import type { RefObject } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { COUNTRY } from 'src/app/shared/constants/configObjectProperties';
import {
  PRODUCT_CONCIERGE,
  PRODUCT_GUARANTEED_HIRE,
  PRODUCT_PREMIUM_PLUS,
} from 'src/app/shared/constants/job-ad-booking-products';
import { SECTION_BOOK_A_CALL } from 'src/app/shared/constants/pageSectionName';
import { getConfig } from 'src/app/utils/config';
import { customRequired, countryPhone } from 'src/app/utils/validation';
import { JobTitle } from 'src/app/v1/views/components/JobTitle';
import { useLocalisedRoutes } from 'src/app/v1/views/hooks/useLocalisedRoutes';
import { PageSection } from 'src/app/v1/views/sections/PageSection';
import type { AppState } from 'src/types/appState';
import type { JobTitleRef, JobLocationRef } from 'src/types/formFields';

import translations from './.vocab';

interface FormFieldsProps {
  onSubmit?: any;
  useField: any;
  isLoading: boolean;
  productType?: string | null;
  jobTitleRef: RefObject<JobTitleRef> | null;
  jobLocationRef: RefObject<JobLocationRef> | null;
  jobCategoryRef: React.RefObject<JobCategoryRef> | null;
}

export const FormFields = ({
  onSubmit,
  useField,
  isLoading,
  productType,
  jobTitleRef,
}: FormFieldsProps) => {
  const country = getConfig(COUNTRY);
  const isGuaranteedHire = productType === PRODUCT_GUARANTEED_HIRE;
  const isConcierge = productType === PRODUCT_CONCIERGE;
  const isPremiumPlus = productType === PRODUCT_PREMIUM_PLUS;
  const { t } = useTranslations(translations);
  const { localiseRoute, resolveUrl } = useLocalisedRoutes();

  const { jobLocation } = useSelector(
    (state: AppState) => ({
      jobLocation: state.session.jobLocationText,
    }),
    shallowEqual,
  );

  const phoneNumber = useField({
    id: 'phoneNumber',
    validators: [customRequired, countryPhone],
    validatorProps: {
      requiredMessage: t('booking-form-phone-field-required'),
      country,
    },
  });
  const userEmail = useField({
    id: 'userEmail',
    validators: [customRequired, isEmail],
    validatorProps: {
      requiredMessage: t('booking-form-email-field-required'),
    },
  });
  const contactName = useField({
    id: 'contactName',
    validators: [customRequired],
    validatorProps: {
      requiredMessage: t('booking-form-contact-name-field-required'),
    },
  });

  const auTermsUrl = resolveUrl({ path: URL_SUPPORT_TERMS, country: 'au' });

  const nzTermsUrl = resolveUrl({ path: URL_SUPPORT_TERMS, country: 'nz' });

  const auProductTermsUrl = resolveUrl({
    path: URL_PRODUCT_TERMS,
    country: 'au',
  });

  const nzProductTermsUrl = resolveUrl({
    path: URL_PRODUCT_TERMS,
    country: 'nz',
  });

  return (
    <form onSubmit={onSubmit}>
      <Stack space="xlarge">
        <Stack space="small">
          <PageSection
            sectionName={SECTION_BOOK_A_CALL}
            testId="job-ad-booking-form"
          >
            <Stack space="small">
              {[
                {
                  id: 'contact-name',
                  label: t('booking-form-contact-name-field-label'),
                  field: contactName,
                },
                {
                  id: 'email',
                  label: t('booking-form-email-field-label'),
                  field: userEmail,
                },
                {
                  id: 'phone',
                  label: t('booking-form-phone-field-label'),
                  field: phoneNumber,
                },
              ].map(({ id, label, field }) => (
                <TextField
                  key={id}
                  id={id}
                  aria-label={id}
                  label={label}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  tone={field.valid ? 'neutral' : 'critical'}
                  message={field.errorMessage}
                  reserveMessageSpace
                />
              ))}

              <JobTitle ref={jobTitleRef} hideHelp={true} disabled />
              <TextField
                key="job-location"
                icon={<IconLocation />}
                id="job-location"
                aria-label="job-location"
                label={t('booking-form-job-location-field-label')}
                value={jobLocation}
                onChange={() => {}}
                disabled
                reserveMessageSpace
              />
            </Stack>
          </PageSection>
          {isGuaranteedHire && (
            <Text>
              {t('booking-form-advertising-terms-and-promise', {
                Terms: (children) => (
                  <TextLink
                    href={localiseRoute(URL_SUPPORT_TERMS)}
                    target="_blank"
                  >
                    {children}
                  </TextLink>
                ),
                MoneyBackPromise: (children) => (
                  <TextLink
                    href={localiseRoute(URL_PRODUCTS_GUARANTEED_HIRE)}
                    target="_blank"
                  >
                    {children}
                  </TextLink>
                ),
              })}
            </Text>
          )}
          {isConcierge && (
            <>
              <Stack space="medium">
                <Text>{t('booking-form-advertising-terms')}</Text>
                <Stack space="small">
                  <Text>
                    {t('booking-form-advertising-terms-anz', {
                      AULink: (children) => (
                        <TextLink href={auTermsUrl} target="_blank">
                          {children}
                        </TextLink>
                      ),
                      NZLink: (children) => (
                        <TextLink href={nzTermsUrl} target="_blank">
                          {children}
                        </TextLink>
                      ),
                    })}
                  </Text>
                  <Text>
                    {t('booking-form-advertising-product-terms-anz', {
                      AULink: (children) => (
                        <TextLink href={auProductTermsUrl} target="_blank">
                          {children}
                        </TextLink>
                      ),
                      NZLink: (children) => (
                        <TextLink href={nzProductTermsUrl} target="_blank">
                          {children}
                        </TextLink>
                      ),
                    })}
                  </Text>
                </Stack>
              </Stack>
            </>
          )}
          {isPremiumPlus && (
            <Text>
              {t('booking-form-advertising-terms-premium-plus', {
                Terms: (children) => (
                  <TextLink
                    href={localiseRoute(URL_SUPPORT_TERMS)}
                    target="_blank"
                  >
                    {children}
                  </TextLink>
                ),
              })}
            </Text>
          )}
        </Stack>
        <Box paddingX={['gutter', 'none']}>
          <Actions>
            <Button
              type="submit"
              id="submit-form"
              loading={isLoading}
              tone="formAccent"
            >
              {t('booking-form-submit-button-label')}
            </Button>
          </Actions>
        </Box>
      </Stack>
    </form>
  );
};
